import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Select,
  SelectItem,
  Tabs,
  Tab,
  Chip,
  Switch,
} from '@nextui-org/react';
import ReactApexChart from 'react-apexcharts';
import ChartDownloadButton from '../../../../../common/src/components/charts/ChartDownloadButton';
import { ApexOptions } from 'apexcharts';
import { COLORS } from 'common/src/utils/colors';
import {
  getSettlementsFobVsExporters,
  getSettlementsFobVsExportersRPC,
  getUniqueCalibersWithCount,
  getUniqueVarietiesWithCount,
  getSentMarketsByExporter,
  getSentShipTripsByExporter,
  checkLabelCompliance,
} from 'common/src/api/settlements';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import ModalSlideFilter from '../../../../../common/src/components/filters/modal-sidebar-filter';
import { Button } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { Popover, PopoverTrigger, PopoverContent } from '@nextui-org/react';
import * as XLSX from 'xlsx';
import { logUserAction } from '../../../services/actionsService';
import useFilterStore from '../../../../../common/src/stores/useFiltersStore';
import SeasonFilter from '../../../../../common/src/components/filters/SeasonFilter';
import MethologyFobExportersModal from '../../../components/modals/MethologyFobExportersModal';
import Header from '../../../components/layouts/Header';
import {
  Alert,
  EmptyState,
  FiltersToggle,
  LoadingState,
  PageLayout,
} from '../../../components/ui';
import useDataStore from '../../../../../common/src/stores/useDataStore';
import useSharingStore from '../../../store/useSharingStore';
import { useTheme } from '../../../hooks/useTheme';
import { formatDate } from '../../../utils/functions';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';
import { getSeasonWeeksForSpecies } from 'common/src/utils/seasonWeeks';

interface BoxPlotData {
  own:
    | {
        label?: string;
        receiver?: string;
        value: number;
        kilos: number;
      }[]
    | null;
  others: {
    min: number;
    q1: number;
    median: number;
    q3: number;
    max: number;
  };
  othersKg?: number;
}

interface WeekData {
  [caliber: string]: BoxPlotData;
}

interface VarietyData {
  [week: string]: WeekData;
}

interface ComparativeData {
  [variety: string]: VarietyData;
}

// Chart types for the unified view
type ChartType = 'global' | 'caliber' | 'variety' | 'evolution';

const CALIBERS = ['L', 'XL', 'J', '2J', '3J', '4J', '5J'];
const SUPPORTED_SPECIES_IDS = [2];
const GLOBAL_DATA_KEY = '__global__';
const GLOBAL_CATEGORY = 'Global';

const shouldUseRpcDataSource = () => {
  if (typeof window === 'undefined') return true;
  const params = new URLSearchParams(window.location.search);
  const source = params.get('source')?.toLowerCase();
  const rpc = params.get('rpc')?.toLowerCase();
  if (['edge', 'function', 'legacy', 'old'].includes(source || '')) {
    return false;
  }
  if (['false', '0', 'edge', 'function', 'legacy', 'old'].includes(rpc || '')) {
    return false;
  }
  return true;
};

const sortCalibersAscending = (values: string[]) =>
  [...values].sort((a, b) => {
    const aNumber = Number(a);
    const bNumber = Number(b);
    const aIsNumeric = a.trim() !== '' && !Number.isNaN(aNumber);
    const bIsNumeric = b.trim() !== '' && !Number.isNaN(bNumber);

    if (aIsNumeric && bIsNumeric) return aNumber - bNumber;
    if (aIsNumeric) return -1;
    if (bIsNumeric) return 1;
    return a.localeCompare(b);
  });

// Color palette for separated own data (table rows + chart scatter points)
const OWN_ROW_COLORS = [
  {
    bg: 'bg-green-50 dark:bg-green-950',
    text: 'text-green-700 dark:text-green-300',
    hex: '#16a34a',
  },
  {
    bg: 'bg-blue-50 dark:bg-blue-950',
    text: 'text-blue-700 dark:text-blue-300',
    hex: '#2563eb',
  },
  {
    bg: 'bg-purple-50 dark:bg-purple-950',
    text: 'text-purple-700 dark:text-purple-300',
    hex: '#9333ea',
  },
  {
    bg: 'bg-amber-50 dark:bg-amber-950',
    text: 'text-amber-700 dark:text-amber-300',
    hex: '#d97706',
  },
  {
    bg: 'bg-rose-50 dark:bg-rose-950',
    text: 'text-rose-700 dark:text-rose-300',
    hex: '#e11d48',
  },
  {
    bg: 'bg-teal-50 dark:bg-teal-950',
    text: 'text-teal-700 dark:text-teal-300',
    hex: '#0d9488',
  },
  {
    bg: 'bg-indigo-50 dark:bg-indigo-950',
    text: 'text-indigo-700 dark:text-indigo-300',
    hex: '#4f46e5',
  },
  {
    bg: 'bg-cyan-50 dark:bg-cyan-950',
    text: 'text-cyan-700 dark:text-cyan-300',
    hex: '#0891b2',
  },
];

const SettlementsComparativeExportersUnified = () => {
  const { t } = useTranslation('settlementsComparative');
  const { selectedSeason } = useFilterStore();
  const { selectedSpecies } = useDataStore();
  const { session, userProfile } = useUserStore();
  const { fetchExceptions, isRestricted, exceptions } = useSharingStore();
  const { isDark } = useTheme();
  const useRpcDataSource = useMemo(shouldUseRpcDataSource, []);

  // Access control state
  const [hasAccess, setHasAccess] = useState(true);
  const configuredWeeks = useMemo(
    () => getSeasonWeeksForSpecies(selectedSpecies?.id, selectedSeason),
    [selectedSpecies?.id, selectedSeason]
  );
  const isSupportedSpecies = useMemo(
    () =>
      selectedSpecies?.id
        ? SUPPORTED_SPECIES_IDS.includes(selectedSpecies.id)
        : false,
    [selectedSpecies?.id]
  );

  // Data states
  const [data, setData] = useState<ComparativeData>({});
  const [dataFetchedWith, setDataFetchedWith] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [varieties, setVarieties] = useState<
    Array<{ variety: string; count: number }>
  >([]);
  const [calibers, setCalibers] = useState<
    Array<{ caliber: string; count: number }>
  >([]);
  const [weeks, setWeeks] = useState<string[]>([]);
  const [markets, setMarkets] = useState<
    Array<{ id: string; name: string; country: string }>
  >([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [shipTrips, setShipTrips] = useState<
    Array<{
      id: number;
      eta: string;
      etd: string;
      ship_name: string;
      market_id: string;
      market_name: string;
      market_country: string;
    }>
  >([]);
  const [marketsAndVarietiesLoading, setMarketsAndVarietiesLoading] =
    useState(false);

  // UI states
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [isMethodologyModalOpen, setIsMethodologyModalOpen] = useState(false);
  const industryChartRef = useRef<HTMLDivElement>(null);
  const [selectedChartType, setSelectedChartType] =
    useState<ChartType>('evolution');
  const canUseCurrentChartType =
    hasAccess && Boolean(selectedSpecies?.id) && Boolean(session);
  const canUseSpeciesSpecificControls =
    canUseCurrentChartType && isSupportedSpecies;
  const availableCalibers = useMemo(
    () =>
      isSupportedSpecies
        ? CALIBERS
        : sortCalibersAscending(calibers.map((item) => item.caliber)),
    [calibers, isSupportedSpecies]
  );
  const renderTabTitle = (label: string, showSoonChip = false) => (
    <div className='flex items-center gap-1.5'>
      <span>{label}</span>
      {showSoonChip && (
        <Chip
          size='sm'
          variant='flat'
          color='warning'
          className='h-4 px-1 text-[10px] leading-none'
        >
          {t('Pronto')}
        </Chip>
      )}
    </div>
  );

  // Filter states
  const [selectedWeek, setSelectedWeek] = useState<number>(2);
  const [selectedVariety, setSelectedVariety] = useState<string>('');
  const [selectedCaliber, setSelectedCaliber] = useState<string>('2J');
  const [selectedCountry, setSelectedCountry] = useState<string[]>([]);
  const [selectedMarket, setSelectedMarket] = useState<string[]>([]);
  const [selectedShipTrip, setSelectedShipTrip] = useState<string[]>([]);
  const [filterMode, setFilterMode] = useState<
    'country' | 'market' | 'shiptrip'
  >('country');
  const [priceView, setPriceView] = useState<'kg' | 'box'>('kg');
  const [visualization, setVisualization] = useState<'chart' | 'table'>(
    'chart'
  );
  const [methodology, setMethodology] = useState<'by_volume' | 'by_exporter'>(
    'by_exporter'
  );
  const [dateMethodology, setDateMethodology] = useState<
    'date_sold' | 'eta' | 'etd'
  >('eta');
  const [incoterm, setIncoterm] = useState<'fob' | 'cif'>('fob');
  const [includeRevenues, setIncludeRevenues] = useState(false);
  const [sinisterFilter, setSinisterFilter] = useState<
    'all' | 'sinister' | 'non_sinister'
  >('all');
  const [separation, setSeparation] = useState<
    'none' | 'by_label' | 'by_receiver'
  >('none');
  const [separationItems, setSeparationItems] = useState<string[]>([]);
  const [selectedSeparationItems, setSelectedSeparationItems] = useState<
    string[]
  >([]);
  const [labelCategoryFilter, setLabelCategoryFilter] = useState<string[]>([]);
  const [labelCompliance, setLabelCompliance] = useState<{
    hasLabels: boolean;
    isCompliant: boolean;
    nonCompliantLabels: string[];
    categories: string[];
  } | null>(null);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const canUseRpcForCurrentRequest =
    useRpcDataSource &&
    Boolean(userProfile?.exporter_id) &&
    Boolean(selectedSpecies?.id);

  useEffect(() => {
    if (!isSupportedSpecies && selectedChartType !== 'global') {
      setSelectedChartType('global');
    }
  }, [isSupportedSpecies, selectedChartType]);

  // Pending filter states for confirmation
  const [pendingCountry, setPendingCountry] = useState<string[]>([]);
  const [pendingMarket, setPendingMarket] = useState<string[]>([]);
  const [pendingShipTrip, setPendingShipTrip] = useState<string[]>([]);
  const [hasPendingChanges, setHasPendingChanges] = useState<boolean>(false);

  // Fetch settlements data
  const fetchSettlementsData = useCallback(async () => {
    try {
      setLoading(true);
      let fetchedData;
      if (canUseRpcForCurrentRequest) {
        const rpcWeekNumber =
          selectedChartType === 'caliber' || selectedChartType === 'variety'
            ? selectedWeek
            : undefined;
        const rpcVariety =
          selectedChartType === 'evolution' ? selectedVariety : undefined;
        const rpcAggregation =
          selectedChartType === 'global' ? 'global' : undefined;

        fetchedData = await getSettlementsFobVsExportersRPC(
          Number(userProfile?.exporter_id),
          methodology,
          selectedSeason,
          selectedSpecies?.id,
          sinisterFilter,
          separation,
          rpcWeekNumber,
          rpcVariety,
          dateMethodology,
          incoterm,
          selectedCountry,
          selectedMarket,
          selectedShipTrip,
          labelCategoryFilter,
          rpcAggregation,
          includeRevenues
        );
      } else if (selectedChartType === 'global') {
        fetchedData = await getSettlementsFobVsExporters(
          session,
          methodology,
          selectedSeason,
          selectedSpecies?.id,
          sinisterFilter,
          separation,
          undefined,
          undefined,
          dateMethodology,
          incoterm,
          selectedCountry,
          selectedMarket,
          selectedShipTrip,
          labelCategoryFilter,
          'global',
          includeRevenues
        );
      } else if (selectedChartType === 'evolution') {
        fetchedData = await getSettlementsFobVsExporters(
          session,
          methodology,
          selectedSeason,
          selectedSpecies?.id,
          sinisterFilter,
          separation,
          undefined, // no specific week
          selectedVariety,
          dateMethodology,
          incoterm,
          selectedCountry,
          selectedMarket,
          selectedShipTrip,
          labelCategoryFilter,
          undefined,
          includeRevenues
        );
      } else {
        fetchedData = await getSettlementsFobVsExporters(
          session,
          methodology,
          selectedSeason,
          selectedSpecies?.id,
          sinisterFilter,
          separation,
          selectedWeek,
          undefined, // no specific variety filter - get all varieties
          dateMethodology,
          incoterm,
          selectedCountry,
          selectedMarket,
          selectedShipTrip,
          labelCategoryFilter,
          undefined,
          includeRevenues
        );
      }
      if (!fetchedData) {
        throw new Error('No response received from server');
      }
      setData(fetchedData);
      setDataFetchedWith(selectedChartType);
    } catch (error) {
      console.error('❌ Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [
    hasPendingChanges,
    canUseRpcForCurrentRequest,
    selectedSpecies?.id,
    selectedChartType,
    selectedVariety,
    userProfile?.exporter_id,
    selectedSeason,
    methodology,
    sinisterFilter,
    separation,
    selectedWeek,
    dateMethodology,
    incoterm,
    includeRevenues,
    selectedCountry,
    selectedMarket,
    selectedShipTrip,
    labelCategoryFilter,
  ]);

  //EVOLUTION USE EFFECT
  useEffect(() => {
    if (selectedChartType !== 'evolution') {
      console.log('❌ Non evolution chart, skipping useEffect trigger');
      return;
    }

    console.log(
      'Calling with:',
      JSON.stringify({
        methodology,
        dateMethodology,
        incoterm,
        includeRevenues,
        separation,
        sinisterFilter,
        selectedVariety,
        selectedCountry,
        selectedMarket,
        selectedShipTrip,
        fetchSettlementsData,
        marketsAndVarietiesLoading,
      })
    );

    if (hasPendingChanges) {
      console.log('❌ Has pending changes, skipping API call');
      return;
    }

    if (!session) {
      console.log('❌ No session, skipping');
      return;
    }

    // For evolution chart, we need a selected variety
    if (!selectedVariety) {
      console.log(
        '❌ Evolution chart needs variety, skipping useEffect trigger'
      );
      return;
    }
    const shouldFetch = !marketsAndVarietiesLoading;
    if (shouldFetch) {
      fetchSettlementsData();
    }
  }, [
    methodology,
    dateMethodology,
    incoterm,
    includeRevenues,
    separation,
    sinisterFilter,
    selectedVariety,
    selectedCountry,
    selectedMarket,
    selectedShipTrip,
    labelCategoryFilter,
    marketsAndVarietiesLoading,
  ]);

  //NON EVOLUTION USE EFFECT
  useEffect(() => {
    if (selectedChartType == 'evolution') {
      console.log('❌ Evolution chart, skipping useEffect trigger');
      return;
    }

    if (hasPendingChanges) {
      console.log('❌ Has pending changes, skipping API call');
      return;
    }

    if (!session) {
      console.log('❌ No session, skipping');
      return;
    }

    if (selectedChartType !== 'global' && !selectedWeek) {
      console.log(
        '❌ Non Evolution chart needs week, skipping useEffect trigger'
      );
      return;
    }

    const shouldFetch = !marketsAndVarietiesLoading;

    if (shouldFetch) {
      fetchSettlementsData();
    }
  }, [
    methodology,
    selectedChartType,
    dateMethodology,
    incoterm,
    includeRevenues,
    separation,
    sinisterFilter,
    selectedWeek,
    selectedCountry,
    selectedMarket,
    selectedShipTrip,
    labelCategoryFilter,
    marketsAndVarietiesLoading,
  ]);

  //CHART TYPE MOVE
  useEffect(() => {
    if (
      dataFetchedWith !== 'evolution' &&
      selectedChartType !== 'evolution' &&
      dataFetchedWith !== 'global' &&
      selectedChartType !== 'global'
    ) {
      console.log('❌ Not needed');
      return;
    }

    if (dataFetchedWith == selectedChartType) {
      console.log('❌ Not needed');
      return;
    }

    // Don't fetch while markets/varieties are still loading
    if (marketsAndVarietiesLoading) return;

    // For evolution chart, require variety to be selected
    if (selectedChartType === 'evolution' && !selectedVariety) return;

    // For non-evolution charts except global, require week to be selected
    if (
      selectedChartType !== 'evolution' &&
      selectedChartType !== 'global' &&
      !selectedWeek
    )
      return;

    fetchSettlementsData();
  }, [selectedChartType, fetchSettlementsData]);

  //Fetch markets, countries, ship trips and varieties when season or species changes
  useEffect(() => {
    const fetchMarketsDataAndVarieties = async () => {
      try {
        setMarketsAndVarietiesLoading(true);
        setSelectedVariety('');
        setSelectedCaliber(isSupportedSpecies ? '2J' : '');
        setVarieties([]);
        setCalibers([]);
        setSelectedCountry([]);
        setSelectedMarket([]);
        setSelectedShipTrip([]);
        setPendingCountry([]);
        setPendingMarket([]);
        setPendingShipTrip([]);
        setHasPendingChanges(false);

        if (!session || !selectedSpecies?.id) {
          setMarketsAndVarietiesLoading(false);
          return;
        }

        const [marketsData, shipTripsData, varietiesData, calibersData] =
          await Promise.all([
            getSentMarketsByExporter(
              session,
              selectedSeason,
              selectedSpecies.id
            ),
            getSentShipTripsByExporter(
              session,
              selectedSeason,
              selectedSpecies.id
            ),
            getUniqueVarietiesWithCount(
              session,
              selectedSeason,
              selectedSpecies.id
            ),
            userProfile?.exporter_id
              ? getUniqueCalibersWithCount(
                  session,
                  selectedSeason,
                  selectedSpecies.id,
                  userProfile.exporter_id
                )
              : Promise.resolve([]),
          ]);

        if (userProfile?.exporter?.hasDemo) {
          setVarieties([{ variety: 'REGINA', count: 100 }]);
          setCalibers([{ caliber: '4J', count: 100 }]);
          setSelectedVariety('REGINA');
          setSelectedCaliber('4J');
          setSelectedChartType('evolution');
        } else if (varietiesData.length > 0) {
          setVarieties(varietiesData);
          const preferredVariety = varietiesData.find(
            (v) => v.variety === 'LAPINS'
          )
            ? 'LAPINS'
            : varietiesData[0].variety;
          setSelectedVariety(preferredVariety);
        }

        if (!userProfile?.exporter?.hasDemo) {
          setCalibers(calibersData);
          const sortedCalibers = sortCalibersAscending(
            calibersData.map((item) => item.caliber)
          );
          const nextCaliber = isSupportedSpecies
            ? '2J'
            : sortedCalibers[0] || '';
          setSelectedCaliber(nextCaliber);
        }

        setMarkets(marketsData);
        setShipTrips(shipTripsData);

        // Extract unique countries from markets
        const uniqueCountries = [
          ...new Set(marketsData.map((market) => market.country)),
        ].sort();
        setCountries(uniqueCountries);
      } catch (error) {
        console.error('Error fetching markets data:', error);
        setMarkets([]);
        setCountries([]);
        setShipTrips([]);
        setVarieties([]);
        setCalibers([]);
      } finally {
        setMarketsAndVarietiesLoading(false);
      }
    };

    if (session && selectedSpecies?.id) {
      fetchMarketsDataAndVarieties();
    }
  }, [
    selectedSeason,
    selectedSpecies?.id,
    isSupportedSpecies,
    userProfile?.exporter?.hasDemo,
    userProfile?.exporter_id,
  ]);

  // Check label compliance when season or species changes
  useEffect(() => {
    const fetchCompliance = async () => {
      if (!session || !selectedSpecies?.id) {
        setLabelCompliance(null);
        setLabelCategoryFilter([]);
        return;
      }
      setComplianceLoading(true);
      try {
        const result = await checkLabelCompliance(
          session,
          selectedSpecies.id,
          selectedSeason
        );
        setLabelCompliance(result);
        // Reset category filter when compliance data changes
        setLabelCategoryFilter([]);
      } catch (error) {
        console.error('Error checking label compliance:', error);
        setLabelCompliance(null);
      } finally {
        setComplianceLoading(false);
      }
    };
    fetchCompliance();
  }, [selectedSeason, selectedSpecies?.id]);

  // Fetch and set weeks when data changes
  useEffect(() => {
    if (selectedChartType === 'evolution' && selectedVariety) {
      const allWeeks = new Set<string>();
      Object.entries(data[selectedVariety] || {}).forEach(([week]) => {
        allWeeks.add(week);
      });

      const sortedWeeks = Array.from(allWeeks).sort((a, b) => {
        const weekA = parseInt(a.split('-W')[1]);
        const weekB = parseInt(b.split('-W')[1]);
        if (weekA >= 27 && weekB < 27) return -1;
        if (weekA < 27 && weekB >= 27) return 1;
        return weekA - weekB;
      });

      setWeeks(sortedWeeks);
    }
  }, [data, selectedChartType, selectedVariety]);

  // Extract unique labels/receivers when data or separation changes
  useEffect(() => {
    if (separation === 'none') {
      setSeparationItems([]);
      setSelectedSeparationItems([]);
      return;
    }

    const uniqueItems = new Set<string>();
    Object.values(data).forEach((varietyData) => {
      Object.values(varietyData || {}).forEach((weekData) => {
        Object.values(weekData || {}).forEach((caliberData) => {
          caliberData?.own?.forEach((item) => {
            const identifier =
              separation === 'by_label' ? item.label : item.receiver;
            if (identifier) uniqueItems.add(identifier);
          });
        });
      });
    });

    const sorted = Array.from(uniqueItems).sort();
    setSeparationItems(sorted);
    setSelectedSeparationItems(sorted);
  }, [data, separation]);

  // Helper to filter own data based on selected separation items
  const filterOwnData = useCallback(
    (
      ownData:
        | { label?: string; receiver?: string; value: number; kilos: number }[]
        | null
    ) => {
      if (!ownData) return ownData;
      if (separation === 'none') return ownData;
      return ownData.filter((item) => {
        const identifier =
          separation === 'by_label' ? item.label : item.receiver;
        return identifier ? selectedSeparationItems.includes(identifier) : true;
      });
    },
    [separation, selectedSeparationItems]
  );

  // Utility function to calculate percentiles
  const calculatePercentile = (
    value: number | null,
    boxPlotData: {
      min: number;
      q1: number;
      median: number;
      q3: number;
      max: number;
    }
  ) => {
    if (!value) return null;
    if (value >= boxPlotData.max) return 100;
    if (value <= boxPlotData.min) return 0;

    if (value <= boxPlotData.q1) {
      return Math.round(
        ((value - boxPlotData.min) / (boxPlotData.q1 - boxPlotData.min)) * 25
      );
    } else if (value <= boxPlotData.median) {
      return Math.round(
        25 +
          ((value - boxPlotData.q1) / (boxPlotData.median - boxPlotData.q1)) *
            25
      );
    } else if (value <= boxPlotData.q3) {
      return Math.round(
        50 +
          ((value - boxPlotData.median) /
            (boxPlotData.q3 - boxPlotData.median)) *
            25
      );
    } else {
      return Math.round(
        75 +
          ((value - boxPlotData.q3) / (boxPlotData.max - boxPlotData.q3)) * 25
      );
    }
  };

  // Helper to get ISO week number from a date string
  const getISOWeekNumber = (dateStr: string): number => {
    const date = new Date(dateStr);
    const d = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    );
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  };

  // Filter confirmation functions
  const confirmFilterChanges = () => {
    setSelectedCountry(filterMode === 'country' ? pendingCountry : []);
    setSelectedMarket(filterMode === 'market' ? pendingMarket : []);
    setSelectedShipTrip(filterMode === 'shiptrip' ? pendingShipTrip : []);

    // Auto-select week when a ship trip is selected (for caliber/variety tabs)
    if (filterMode === 'shiptrip' && pendingShipTrip.length > 0) {
      const firstTripId = pendingShipTrip[0];
      const trip = shipTrips.find((t) => t.id.toString() === firstTripId);
      if (trip) {
        const relevantDate = dateMethodology === 'etd' ? trip.etd : trip.eta;
        if (relevantDate) {
          const weekNum = getISOWeekNumber(relevantDate);
          if (configuredWeeks.includes(weekNum)) {
            setSelectedWeek(weekNum);
          }
        }
      }
    }

    setHasPendingChanges(false);
  };

  // Auto-update selectedWeek when dateMethodology changes while a ship trip is active
  useEffect(() => {
    if (selectedShipTrip.length > 0) {
      const firstTripId = selectedShipTrip[0];
      const trip = shipTrips.find((t) => t.id.toString() === firstTripId);
      if (trip) {
        const relevantDate = dateMethodology === 'etd' ? trip.etd : trip.eta;
        if (relevantDate) {
          const weekNum = getISOWeekNumber(relevantDate);
          if (configuredWeeks.includes(weekNum)) {
            setSelectedWeek(weekNum);
          }
        }
      }
    }
  }, [configuredWeeks, dateMethodology, selectedShipTrip, shipTrips]);

  const resetPendingChanges = () => {
    setPendingCountry(selectedCountry);
    setPendingMarket(selectedMarket);
    setPendingShipTrip(selectedShipTrip);
    setHasPendingChanges(false);
  };

  const clearPendingChanges = () => {
    setPendingCountry([]);
    setPendingMarket([]);
    setPendingShipTrip([]);
    setHasPendingChanges(false);
  };

  const handleFilterChange = (
    filterType: 'country' | 'market' | 'shiptrip',
    values: string[]
  ) => {
    switch (filterType) {
      case 'country':
        setPendingCountry(values);
        // Reset pending market when country changes
        setPendingMarket([]);
        break;
      case 'market':
        setPendingMarket(values);
        break;
      case 'shiptrip':
        setPendingShipTrip(values);
        break;
    }
    console.log('Has pending changes:', hasPendingChanges);
    setHasPendingChanges(true);
  };

  const handleFilterModeChange = (
    newMode: 'country' | 'market' | 'shiptrip'
  ) => {
    // First set the new filter mode
    console.log('handleFilterModeChange', newMode);
    setFilterMode(newMode);

    // Clear pending selections and show confirm button if any were selected
    const hasAnySelection =
      pendingCountry.length > 0 ||
      pendingMarket.length > 0 ||
      pendingShipTrip.length > 0;

    setPendingCountry([]);
    setPendingMarket([]);
    setPendingShipTrip([]);

    if (hasAnySelection) {
      console.log('Has any selection:', hasAnySelection);
      setHasPendingChanges(true);
    }
  };

  // Reset selections when filter mode changes
  useEffect(() => {
    setPendingCountry([]);
    setPendingMarket([]);
    setPendingShipTrip([]);
  }, [filterMode]);

  // Initialize pending states when component mounts
  useEffect(() => {
    console.log('Initializing pending states on mount');
    setPendingCountry(selectedCountry);
    setPendingMarket(selectedMarket);
    setPendingShipTrip(selectedShipTrip);
  }, []); // Only run on mount

  useEffect(() => {
    logUserAction('view', 'page', 'settlements-comparative-exporters-unified');
  }, []);

  // Fetch sharing exceptions when user profile is available
  useEffect(() => {
    if (userProfile?.exporter_id) {
      fetchExceptions(userProfile.exporter_id);
    }
  }, [userProfile?.exporter_id, fetchExceptions]);

  // Check if access is restricted for current species/season
  useEffect(() => {
    if (!selectedSpecies || !userProfile || !selectedSeason) {
      setHasAccess(true);
      return;
    }

    const restricted = isRestricted(selectedSpecies.id, selectedSeason);
    setHasAccess(!restricted);
  }, [selectedSpecies, selectedSeason, userProfile, isRestricted, exceptions]);

  // Base chart options
  const getBaseChartOptions = (
    xAxisTitle: string,
    categories: string[],
    priceView: 'kg' | 'box',
    scatterColors?: string[]
  ): ApexOptions => ({
    chart: {
      type: 'boxPlot',
      foreColor: isDark ? '#A1A1AA' : '#71717A',
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: true },
      fontFamily: 'inherit',
      redrawOnParentResize: true,
      redrawOnWindowResize: true,
    },
    colors: [
      COLORS.cherry,
      ...(scatterColors || [
        COLORS.lightGreen,
        COLORS.blue,
        COLORS.gray,
        COLORS.yellow,
      ]),
    ],
    stroke: {
      colors: [isDark ? '#ffffff' : '#000000'],
    },
    plotOptions: {
      boxPlot: {
        colors: {
          upper: COLORS.cherry,
          lower: COLORS.cherry,
        },
      },
    },
    xaxis: {
      type: 'category',
      categories: categories || [],
      title: {
        text: xAxisTitle || '',
        style: {
          fontSize: '14px',
          fontWeight: 600,
          color: '#374151',
        },
      },
      labels: {
        ...(selectedChartType === 'evolution' ? { rotate: -45 } : {}),
        style: {
          fontSize: '12px',
          fontWeight: 400,
          colors: '#6B7280',
        },
      },
      crosshairs: {
        show: true,
        width: 1,
        opacity: 0.9,
        stroke: { color: '#b6b6b6', width: 1, dashArray: 0 },
        fill: {
          type: 'solid',
          color: '#B1B9C4',
          gradient: {
            colorFrom: '#D8E3F0',
            colorTo: '#BED1E6',
            stops: [0, 100],
            opacityFrom: 0.4,
            opacityTo: 0.5,
          },
        },
        dropShadow: { enabled: false, top: 0, left: 0, blur: 1, opacity: 0.4 },
      },
      tooltip: { enabled: false },
    },
    yaxis: {
      title: {
        text:
          priceView === 'kg'
            ? `${incoterm === 'fob' ? 'FOB' : 'CIF'} USD/kg`
            : `${incoterm === 'fob' ? 'FOB' : 'CIF'} USD/Caja`,
        style: {
          fontSize: '14px',
          fontWeight: 600,
          color: '#374151',
        },
      },
      labels: {
        formatter: (value) => `$${value ? value.toFixed(2) : 0}`,
        style: {
          fontSize: '12px',
          fontWeight: 400,
          colors: '#6B7280',
        },
      },
      forceNiceScale: true,
      decimalsInFloat: 2,
    },
    tooltip: {
      intersect: false,
      shared: true,
      followCursor: true,
      fixed: { enabled: false },
      style: {
        fontSize: '12px',
        fontFamily: 'inherit',
      },
      cssClass: 'apexcharts-tooltip-top-fixed',
    },
    grid: {
      show: true,
      xaxis: { lines: { show: true } },
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: true,
      fontSize: '12px',
      fontFamily: 'inherit',
      fontWeight: 400,
    },
  });

  // Generate tooltip HTML
  const generateTooltipHtml = (
    title: string,
    currentData: any,
    priceView: 'kg' | 'box',
    separation: 'none' | 'by_label' | 'by_receiver',
    calculatePercentile: Function
  ) => {
    if (!currentData) return '';

    const ownValues = filterOwnData(currentData?.own) || [];
    const caliberOthersKg = currentData?.othersKg || 0;
    const multiplier = priceView === 'box' ? 5 : 1;

    const ownValuesSection = ownValues
      .map((item: any, i: number) => {
        const label =
          separation === 'by_label'
            ? item.label
            : separation === 'by_receiver'
              ? item.receiver
              : t('Propio');
        const value = item.value;

        // Pick the matching color from the palette
        const identifier =
          separation === 'by_label'
            ? item.label
            : separation === 'by_receiver'
              ? item.receiver
              : null;
        const colorIdx = identifier
          ? selectedSeparationItems.indexOf(identifier)
          : 0;
        const itemColor =
          OWN_ROW_COLORS[colorIdx >= 0 ? colorIdx % OWN_ROW_COLORS.length : 0]
            .hex;

        const percentile =
          caliberOthersKg > 0
            ? calculatePercentile(value, {
                min: currentData.others.min,
                q1: currentData.others.q1,
                median: currentData.others.median,
                q3: currentData.others.q3,
                max: currentData.others.max,
              })
            : null;

        return `
        <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
          <span style="color: ${itemColor}; font-weight: bold;">${label}</span>
          <span style="font-weight: bold; color: #000000; margin-left: 10px;">$${
            !isNaN(Number(value))
              ? (value * multiplier).toFixed(2)
              : t('No definido')
          }</span>
        </div>
        ${
          percentile !== null
            ? `
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
            <span style="color: ${itemColor}; font-weight: bold;">${t('Percentil')}</span>
            <span style="font-weight: bold; color: #000000; margin-left: 10px;">${
              percentile >= 0 ? percentile : t('No definido')
            }</span>
          </div>
        `
            : ''
        }
        <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
          <span style="color: ${itemColor}; font-weight: bold;">${t('Kilos')}</span>
          <span style="font-weight: bold; color: #000000; margin-left: 10px;">${item.kilos.toLocaleString(
            'es-CL'
          )} kg</span>
        </div>
        ${
          ownValues.length > 1
            ? '<div style="border-top: 1px solid #eee; margin: 8px 0;"></div>'
            : ''
        }
      `;
      })
      .join('');

    const boxPlotSection =
      caliberOthersKg > 0
        ? `
      <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
        <span style="color: #666;">${t('Máximo')}:</span>
        <span style="font-weight: bold; color: #000000; margin-left: 10px;">$${
          currentData.others.max
            ? (currentData.others.max * multiplier).toFixed(2)
            : t('No definido')
        }</span>
      </div>
      <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
        <span style="color: #666;">Q3:</span>
        <span style="font-weight: bold; color: #000000; margin-left: 10px;">$${
          currentData.others.q3
            ? (currentData.others.q3 * multiplier).toFixed(2)
            : t('No definido')
        }</span>
      </div>
      <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
        <span style="color: #666;">${t('Mediana')}:</span>
        <span style="font-weight: bold; color: #000000; margin-left: 10px;">$${
          currentData.others.median
            ? (currentData.others.median * multiplier).toFixed(2)
            : t('No definido')
        }</span>
      </div>
      <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
        <span style="color: #666;">Q1:</span>
        <span style="font-weight: bold; color: #000000; margin-left: 10px;">$${
          currentData.others.q1
            ? (currentData.others.q1 * multiplier).toFixed(2)
            : t('No definido')
        }</span>
      </div>
      <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
        <span style="color: #666;">${t('Mínimo')}:</span>
        <span style="font-weight: bold; color: #000000; margin-left: 10px;">$${
          currentData.others.min
            ? (currentData.others.min * multiplier).toFixed(2)
            : t('No definido')
        }</span>
      </div>
    `
        : '';

    return `
      <div style="background-color: #f8f9fa; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); padding: 12px; font-family: Arial, sans-serif;">
        <div style="text-align: center; font-size: 16px; font-weight: bold; color: #333; margin-bottom: 2px;">
          ${title}
        </div>
        ${boxPlotSection}
        ${
          ownValues.length > 0
            ? `
          ${
            boxPlotSection
              ? '<div style="border-top: 1px solid #eee; margin: 8px 0;"></div>'
              : ''
          }
          ${ownValuesSection}
        `
            : `
          <div style="font-size: 12px; color: #666; text-align: center;">
            ${t('No hay datos propios para este elemento')}
          </div>
        `
        }
        ${
          caliberOthersKg > 0
            ? `
          <div style="border-top: 1px solid #eee; margin: 8px 0;"></div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
            <span style="color: #666;">${t('Kilos Totales Otras')}:</span>
            <span style="font-weight: bold; color: #000000; margin-left: 10px;">${caliberOthersKg.toLocaleString(
              'es-CL'
            )} kg</span>
          </div>
        `
            : ''
        }
      </div>
    `;
  };

  const getGlobalBoxData = (): BoxPlotData | undefined =>
    data[GLOBAL_DATA_KEY]?.[GLOBAL_DATA_KEY]?.[GLOBAL_DATA_KEY];

  // Get chart options with custom tooltip
  const getChartOptions = (
    type: ChartType,
    categories: string[],
    priceView: 'kg' | 'box',
    scatterColors?: string[]
  ): ApexOptions => {
    const xAxisTitle =
      type === 'global'
        ? t('Global')
        : type === 'caliber'
          ? t('Calibre')
          : type === 'variety'
            ? t('Variedad')
            : t('Semana');

    const baseOptions = getBaseChartOptions(
      xAxisTitle,
      categories,
      priceView,
      scatterColors
    );

    return {
      ...baseOptions,
      tooltip: {
        ...baseOptions.tooltip,
        custom: function ({ seriesIndex, dataPointIndex, w }) {
          let currentData, title;

          if (type === 'global') {
            currentData = getGlobalBoxData();
            title = t('Global');
          } else if (type === 'evolution') {
            const weekNumber = w.globals.categoryLabels[dataPointIndex];
            const fullWeek = weeks.find((w) => w.split('-W')[1] === weekNumber);
            currentData = fullWeek
              ? data[selectedVariety]?.[fullWeek]?.[selectedCaliber]
              : null;
            title = `${t('Semana')} ${weekNumber}`;
          } else {
            const yearWeek =
              selectedWeek < 27
                ? `${selectedSeason.split('-')[1]}-W${selectedWeek}`
                : `${selectedSeason.split('-')[0]}-W${selectedWeek}`;
            const category = w.globals.categoryLabels[dataPointIndex];

            if (type === 'caliber') {
              currentData = data[selectedVariety]?.[yearWeek]?.[category];
              title = `${t('Calibre')} ${category}`;
            } else {
              currentData = data[category]?.[yearWeek]?.[selectedCaliber];
              title = category;
            }
          }

          return generateTooltipHtml(
            title,
            currentData,
            priceView,
            separation,
            calculatePercentile
          );
        },
      },
    };
  };

  // Prepare series data for charts
  const prepareSeriesData = (type: ChartType) => {
    const multiplier = priceView === 'box' ? 5 : 1;
    let categories: string[] = [];

    if (type === 'global') {
      categories = [t(GLOBAL_CATEGORY)];
    } else if (type === 'caliber') {
      categories = availableCalibers;
    } else if (type === 'variety') {
      categories = varieties.map((v) => v.variety);
    } else if (type === 'evolution') {
      categories = weeks.map((w) => w.split('-W')[1]);
    }

    const boxPlotSeries = {
      name: t('OTRAS EXPORTADORAS'),
      type: 'boxPlot',
      data: categories.map((category) => {
        let categoryData;

        if (type === 'global') {
          categoryData = getGlobalBoxData();
        } else if (type === 'evolution') {
          const fullWeek = weeks.find((w) => w.split('-W')[1] === category);
          categoryData = fullWeek
            ? data[selectedVariety]?.[fullWeek]?.[selectedCaliber]
            : null;
        } else {
          const yearWeek =
            selectedWeek < 27
              ? `${selectedSeason.split('-')[1]}-W${selectedWeek}`
              : `${selectedSeason.split('-')[0]}-W${selectedWeek}`;

          if (type === 'caliber') {
            categoryData = data[selectedVariety]?.[yearWeek]?.[category];
          } else {
            categoryData = data[category]?.[yearWeek]?.[selectedCaliber];
          }
        }

        return {
          x: category,
          y:
            categoryData?.others && categoryData.othersKg > 0
              ? [
                  categoryData.others.min * multiplier,
                  categoryData.others.q1 * multiplier,
                  categoryData.others.median * multiplier,
                  categoryData.others.q3 * multiplier,
                  categoryData.others.max * multiplier,
                ]
              : [null, null, null, null, null],
        };
      }),
    };

    // Generate scatter series for own data points
    const scatterSeries: any[] = [];

    categories.forEach((category) => {
      let categoryData;

      if (type === 'global') {
        categoryData = getGlobalBoxData();
      } else if (type === 'evolution') {
        const fullWeek = weeks.find((w) => w.split('-W')[1] === category);
        categoryData = fullWeek
          ? data[selectedVariety]?.[fullWeek]?.[selectedCaliber]
          : null;
      } else {
        const yearWeek =
          selectedWeek < 27
            ? `${selectedSeason.split('-')[1]}-W${selectedWeek}`
            : `${selectedSeason.split('-')[0]}-W${selectedWeek}`;

        if (type === 'caliber') {
          categoryData = data[selectedVariety]?.[yearWeek]?.[category];
        } else {
          categoryData = data[category]?.[yearWeek]?.[selectedCaliber];
        }
      }

      const filteredOwn = filterOwnData(categoryData?.own || null);
      if (filteredOwn && filteredOwn.length > 0) {
        filteredOwn.forEach((ownData: any) => {
          const seriesName =
            separation === 'by_label'
              ? ownData.label
              : separation === 'by_receiver'
                ? ownData.receiver
                : t('PROPIO');

          let series = scatterSeries.find((s) => s.name === seriesName);
          if (!series) {
            series = {
              name: seriesName,
              type: 'scatter',
              data: categories.map(() => ({ x: '', y: null })),
            };
            scatterSeries.push(series);
          }

          const categoryIndex = categories.indexOf(category);
          if (categoryIndex !== -1) {
            series.data[categoryIndex] = {
              x: category,
              y: ownData.value * multiplier,
            };
          }
        });
      }
    });

    // Build scatter colors matching the series order
    const scatterColors = scatterSeries.map((s) => {
      if (separation === 'none') return OWN_ROW_COLORS[0].hex;
      const idx = selectedSeparationItems.indexOf(s.name);
      return OWN_ROW_COLORS[idx >= 0 ? idx % OWN_ROW_COLORS.length : 0].hex;
    });

    return { series: [boxPlotSeries, ...scatterSeries], scatterColors };
  };

  // Helper function to calculate weighted average FOB
  const calculateWeightedOwnFOB = (
    ownData:
      | {
          label?: string;
          receiver?: string;
          value: number;
          kilos: number;
        }[]
      | null
  ) => {
    if (!ownData?.length) return null;
    const totalKilos = ownData.reduce(
      (sum, item) => sum + (item.kilos || 0),
      0
    );
    if (totalKilos === 0) return null;
    const weightedSum = ownData.reduce(
      (sum, item) => sum + item.value * (item.kilos || 0),
      0
    );
    return weightedSum / totalKilos;
  };

  // Excel helper: get cell value for a column's box data
  const getExcelCellValue = (
    key: string,
    boxData: BoxPlotData | undefined,
    multiplier: number,
    identifier?: string
  ): any => {
    if (!boxData) return null;

    if (key === 'othersKg') {
      return boxData.othersKg ? boxData.othersKg : null;
    }
    if (['max', 'q3', 'median', 'q1', 'min'].includes(key)) {
      const val = boxData.others[key as keyof typeof boxData.others];
      return val ? Number((val * multiplier).toFixed(2)) : null;
    }

    const ownData = filterOwnData(boxData.own || null);

    if (identifier) {
      const item = ownData?.find((d) =>
        separation === 'by_label'
          ? d.label === identifier
          : d.receiver === identifier
      );
      if (!item) return null;
      if (key === 'own') return Number((item.value * multiplier).toFixed(2));
      if (key === 'percentile') {
        const p = calculatePercentile(item.value, boxData.others);
        return p !== null ? Number(p.toFixed(0)) : null;
      }
      if (key === 'ownKg') return item.kilos ?? null;
    } else {
      if (!ownData || ownData.length === 0) return null;
      if (key === 'own') {
        const avgFOB = calculateWeightedOwnFOB(ownData);
        return avgFOB ? Number((avgFOB * multiplier).toFixed(2)) : null;
      }
      if (key === 'percentile') {
        const avgFOB = calculateWeightedOwnFOB(ownData);
        if (!avgFOB) return null;
        const p = calculatePercentile(avgFOB, boxData.others);
        return p !== null ? Number(p.toFixed(0)) : null;
      }
      if (key === 'ownKg') {
        const totalKilos = ownData.reduce(
          (sum, item) => sum + (item.kilos || 0),
          0
        );
        return totalKilos || null;
      }
    }
    return null;
  };

  // Excel helper: build rows for export (same pattern as getTableRows but for Excel labels)
  const getExcelRows = (): {
    key: string;
    label: string;
    identifier?: string;
  }[] => {
    const fobOrCif = incoterm === 'fob' ? 'FOB' : 'CIF';
    const rows: { key: string; label: string; identifier?: string }[] = [];

    if (separation !== 'none' && selectedSeparationItems.length > 0) {
      selectedSeparationItems.forEach((identifier) => {
        rows.push({
          key: 'own',
          label: `${fobOrCif} ${identifier}`,
          identifier,
        });
        rows.push({
          key: 'percentile',
          label: `Percentil ${identifier}`,
          identifier,
        });
        rows.push({ key: 'ownKg', label: `Kilos ${identifier}`, identifier });
      });
    } else {
      rows.push({ key: 'own', label: `${fobOrCif} Propio` });
      rows.push({ key: 'percentile', label: 'Percentil Propio' });
      rows.push({ key: 'ownKg', label: 'Kilos Propios' });
    }

    rows.push(
      { key: 'max', label: 'Máximo' },
      { key: 'q3', label: 'Q3' },
      { key: 'median', label: 'Mediana' },
      { key: 'q1', label: 'Q1' },
      { key: 'min', label: 'Mínimo' },
      { key: 'othersKg', label: 'Kilos Otras' }
    );

    return rows;
  };

  // Export to Excel function
  const exportToExcel = (type: ChartType) => {
    const multiplier = priceView === 'box' ? 5 : 1;
    let fileName = '';
    let excelData: any[] = [];
    const excelRows = getExcelRows();

    if (type === 'global') {
      fileName = `${incoterm === 'fob' ? 'FOB' : 'CIF'}_Global.xlsx`;
      const globalData = getGlobalBoxData();

      excelData.push(['Estadística', t('Global')]);

      excelRows.forEach(({ key, label, identifier }) => {
        excelData.push([
          label,
          getExcelCellValue(key, globalData, multiplier, identifier),
        ]);
      });
    } else if (type === 'caliber') {
      fileName = `${incoterm === 'fob' ? 'FOB' : 'CIF'}_por_Calibre_${selectedVariety}_${selectedWeek}.xlsx`;
      const calibers = availableCalibers;
      const yearWeek =
        selectedWeek < 27
          ? `${selectedSeason.split('-')[1]}-W${selectedWeek}`
          : `${selectedSeason.split('-')[0]}-W${selectedWeek}`;
      const caliberData = data[selectedVariety]?.[yearWeek];

      excelData.push(['Estadística', ...calibers]);

      excelRows.forEach(({ key, label, identifier }) => {
        const rowData: any[] = [label];
        calibers.forEach((caliber) => {
          rowData.push(
            getExcelCellValue(
              key,
              caliberData?.[caliber],
              multiplier,
              identifier
            )
          );
        });
        excelData.push(rowData);
      });
    } else if (type === 'variety') {
      fileName = `${incoterm === 'fob' ? 'FOB' : 'CIF'}_por_Variedad_${selectedCaliber}_Semana${selectedWeek}.xlsx`;
      const varietyNames = varieties.map((v) => v.variety);
      const yearWeek =
        selectedWeek < 27
          ? `${selectedSeason.split('-')[1]}-W${selectedWeek}`
          : `${selectedSeason.split('-')[0]}-W${selectedWeek}`;

      excelData.push(['Estadística', ...varietyNames]);

      excelRows.forEach(({ key, label, identifier }) => {
        const rowData: any[] = [label];
        varietyNames.forEach((variety) => {
          rowData.push(
            getExcelCellValue(
              key,
              data[variety]?.[yearWeek]?.[selectedCaliber],
              multiplier,
              identifier
            )
          );
        });
        excelData.push(rowData);
      });
    } else if (type === 'evolution') {
      fileName = `${incoterm === 'fob' ? 'FOB' : 'CIF'}_Evolución_${selectedVariety}_${selectedCaliber}.xlsx`;

      excelData.push([
        'Estadística',
        ...weeks.map((week) => `Semana ${week.split('-W')[1]}`),
      ]);

      excelRows.forEach(({ key, label, identifier }) => {
        const rowData: any[] = [label];
        weeks.forEach((week) => {
          rowData.push(
            getExcelCellValue(
              key,
              data[selectedVariety]?.[week]?.[selectedCaliber],
              multiplier,
              identifier
            )
          );
        });
        excelData.push(rowData);
      });
    }

    // Create and download Excel file
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    const colWidths = Array(excelData[0]?.length || 0).fill({ wch: 15 });
    colWidths[0] = { wch: 20 };
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, fileName);
  };

  // Helper to build table row definitions dynamically based on separation mode
  const getTableRows = (): {
    key: string;
    label: string;
    isOwn: boolean;
    colorIndex: number;
    identifier?: string;
  }[] => {
    const fobOrCif = incoterm === 'fob' ? 'FOB' : 'CIF';
    const rows: {
      key: string;
      label: string;
      isOwn: boolean;
      colorIndex: number;
      identifier?: string;
    }[] = [];

    if (separation !== 'none' && selectedSeparationItems.length > 0) {
      selectedSeparationItems.forEach((identifier, i) => {
        const colorIndex = i % OWN_ROW_COLORS.length;
        rows.push({
          key: 'own',
          label: `${fobOrCif} ${identifier}`,
          isOwn: true,
          colorIndex,
          identifier,
        });
        rows.push({
          key: 'percentile',
          label: `${t('Percentil')} ${identifier}`,
          isOwn: true,
          colorIndex,
          identifier,
        });
        rows.push({
          key: 'ownKg',
          label: `${t('Kilos')} ${identifier}`,
          isOwn: true,
          colorIndex,
          identifier,
        });
      });
    } else {
      rows.push({
        key: 'own',
        label: `${fobOrCif} ${t('Propio')}`,
        isOwn: true,
        colorIndex: 0,
      });
      rows.push({
        key: 'percentile',
        label: t('Percentil Propio'),
        isOwn: true,
        colorIndex: 0,
      });
      rows.push({
        key: 'ownKg',
        label: t('Kilos Propios'),
        isOwn: true,
        colorIndex: 0,
      });
    }

    rows.push(
      { key: 'max', label: t('Máximo'), isOwn: false, colorIndex: -1 },
      { key: 'q3', label: 'Q3', isOwn: false, colorIndex: -1 },
      { key: 'median', label: t('Mediana'), isOwn: false, colorIndex: -1 },
      { key: 'q1', label: 'Q1', isOwn: false, colorIndex: -1 },
      { key: 'min', label: t('Mínimo'), isOwn: false, colorIndex: -1 },
      { key: 'othersKg', label: t('Kilos Otras'), isOwn: false, colorIndex: -1 }
    );

    return rows;
  };

  // Helper to get a cell value for a given row definition and box plot data
  const getCellValue = (
    row: { key: string; isOwn: boolean; identifier?: string },
    boxData: BoxPlotData | undefined,
    multiplier: number
  ): string => {
    if (!boxData) return '-';

    const { key, identifier } = row;

    // Market statistics rows
    if (key === 'othersKg') {
      return boxData.othersKg
        ? boxData.othersKg.toLocaleString('es-CL') + ' kg'
        : '-';
    }
    if (['max', 'q3', 'median', 'q1', 'min'].includes(key)) {
      const val = boxData.others[key as keyof typeof boxData.others];
      return val ? `$${(val * multiplier).toFixed(2)}` : '-';
    }

    // Own data rows
    const ownData = filterOwnData(boxData.own || null);

    if (identifier) {
      // Separated mode: find the specific item
      const item = ownData?.find((d) =>
        separation === 'by_label'
          ? d.label === identifier
          : d.receiver === identifier
      );
      if (!item) return '-';

      if (key === 'own') {
        return `$${(item.value * multiplier).toFixed(2)}`;
      }
      if (key === 'percentile') {
        const p = calculatePercentile(item.value, boxData.others);
        return p !== null ? `${p}%` : '-';
      }
      if (key === 'ownKg') {
        return item.kilos != null
          ? `${item.kilos.toLocaleString('es-CL')} kg`
          : '-';
      }
    } else {
      // Non-separated mode
      if (!ownData || ownData.length === 0) return '-';

      if (key === 'own') {
        const avgFOB = calculateWeightedOwnFOB(ownData);
        return avgFOB ? `$${(avgFOB * multiplier).toFixed(2)}` : '-';
      }
      if (key === 'percentile') {
        const avgFOB = calculateWeightedOwnFOB(ownData);
        if (!avgFOB) return '-';
        const p = calculatePercentile(avgFOB, boxData.others);
        return p !== null ? `${p}%` : '-';
      }
      if (key === 'ownKg') {
        const totalKilos = ownData.reduce(
          (sum, item) => sum + (item.kilos || 0),
          0
        );
        return totalKilos ? `${totalKilos.toLocaleString('es-CL')} kg` : '-';
      }
    }

    return '-';
  };

  // Table view component
  const TableView = ({ type }: { type: ChartType }) => {
    const multiplier = priceView === 'box' ? 5 : 1;
    const tableRows = getTableRows();

    // Determine columns and data accessor based on chart type
    let columns: { key: string; label: string }[] = [];
    let getBoxData: (colKey: string) => BoxPlotData | undefined;

    if (type === 'global') {
      columns = [{ key: GLOBAL_DATA_KEY, label: t('Global') }];
      getBoxData = () => getGlobalBoxData();
    } else if (type === 'caliber') {
      const yearWeek =
        selectedWeek < 27
          ? `${selectedSeason.split('-')[1]}-W${selectedWeek}`
          : `${selectedSeason.split('-')[0]}-W${selectedWeek}`;
      columns = availableCalibers.map((c) => ({ key: c, label: c }));
      getBoxData = (caliber) => data[selectedVariety]?.[yearWeek]?.[caliber];
    } else if (type === 'variety') {
      const yearWeek =
        selectedWeek < 27
          ? `${selectedSeason.split('-')[1]}-W${selectedWeek}`
          : `${selectedSeason.split('-')[0]}-W${selectedWeek}`;
      columns = varieties.map((v) => ({ key: v.variety, label: v.variety }));
      getBoxData = (variety) => data[variety]?.[yearWeek]?.[selectedCaliber];
    } else {
      columns = weeks.map((w) => ({
        key: w,
        label: `${t('Semana')} ${w.split('-W')[1]}`,
      }));
      getBoxData = (week) => data[selectedVariety]?.[week]?.[selectedCaliber];
    }

    return (
      <div className='overflow-x-auto'>
        <table className='min-w-full border-collapse'>
          <thead>
            <tr className='bg-default-100'>
              <th className='border px-4 py-2'>{t('Estadística')}</th>
              {columns.map((col) => (
                <th key={col.key} className='border px-4 py-2'>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row, idx) => {
              const color = row.isOwn ? OWN_ROW_COLORS[row.colorIndex] : null;
              return (
                <tr
                  key={`${row.key}-${row.identifier ?? 'all'}-${idx}`}
                  className={color ? color.bg : ''}
                >
                  <td
                    className={`border px-4 py-2 font-medium ${
                      color ? color.text : ''
                    }`}
                  >
                    {row.label}
                  </td>
                  {columns.map((col) => (
                    <td
                      key={`${row.key}-${row.identifier ?? 'all'}-${col.key}`}
                      className={`border px-4 py-2 text-center ${
                        color ? `font-semibold ${color.text}` : ''
                      }`}
                    >
                      {getCellValue(row, getBoxData(col.key), multiplier)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Get chart title based on type
  const getChartTitle = (type: ChartType) => {
    switch (type) {
      case 'global':
        return `${incoterm === 'fob' ? 'FOB' : 'CIF'} ${t('Global')}`;
      case 'caliber':
        return `${incoterm === 'fob' ? 'FOB' : 'CIF'} ${t('por Calibre')}, ${selectedVariety} - ${t('Semana')} ${selectedWeek}`;
      case 'variety':
        return `${incoterm === 'fob' ? 'FOB' : 'CIF'} ${t('por Variedad')}, ${t('Calibre')} ${selectedCaliber} - ${t('Semana')} ${selectedWeek}`;
      case 'evolution':
        return `${incoterm === 'fob' ? 'FOB' : 'CIF'} ${t('por Semana')}, ${selectedVariety} - ${t('Calibre')} ${selectedCaliber}`;
      default:
        return '';
    }
  };

  return (
    <div className='flex w-full h-screen overflow-x-hidden'>
      <style>{`
        .apexcharts-tooltip-top-fixed {
          top: 0 !important;
          pointer-events: none;
        }
        .apexcharts-canvas,
        .apexcharts-canvas foreignObject,
        .apexcharts-inner {
          overflow: visible !important;
        }
      `}</style>
      {/* Sticky Sidebar */}
      <ModalSlideFilter
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
      >
        <div className='bg-background p-4 pt-8 h-full overflow-y-auto'>
          <div className='space-y-4'>
            <div className='flex items-center justify-between'>
              <p className='text-sm text-default-500 mb-1'>
                {t('Metodología')}
              </p>
              <Button
                isIconOnly
                size='sm'
                variant='light'
                onPress={() => setIsMethodologyModalOpen(true)}
              >
                <Icon icon='tabler:help-circle' className='text-default-400' />
              </Button>
            </div>

            <Select
              label={t('Ponderación')}
              selectedKeys={[methodology]}
              onSelectionChange={(keys) =>
                setMethodology(
                  Array.from(keys)[0] as 'by_volume' | 'by_exporter'
                )
              }
              className='w-full mb-4'
              isDisabled={!canUseCurrentChartType || loading}
            >
              <SelectItem key='by_volume' value='by_volume'>
                {t('Por Volumen')}
              </SelectItem>
              <SelectItem key='by_exporter' value='by_exporter'>
                {t('Por Exportadora')}
              </SelectItem>
            </Select>

            <Select
              label={t('Fecha de Agrupación')}
              selectedKeys={[dateMethodology]}
              onSelectionChange={(keys) =>
                setDateMethodology(
                  Array.from(keys)[0] as 'date_sold' | 'eta' | 'etd'
                )
              }
              className='w-full mb-4'
              isDisabled={!canUseCurrentChartType || loading}
            >
              <SelectItem key='date_sold' value='date_sold'>
                {t('Fecha Venta Liquidación')}
              </SelectItem>
              <SelectItem key='eta' value='eta'>
                {t('Fecha Arribo Nave')}
              </SelectItem>
              <SelectItem key='etd' value='etd'>
                {t('Fecha Zarpe Nave')}
              </SelectItem>
            </Select>

            <Select
              label={t('Incoterm')}
              selectedKeys={[incoterm]}
              onSelectionChange={(keys) =>
                setIncoterm(Array.from(keys)[0] as 'fob' | 'cif')
              }
              className='w-full mb-4'
              isDisabled={!canUseCurrentChartType || loading}
            >
              <SelectItem
                key='fob'
                value='fob'
                description={t('Incluye el costo de flete marítimo/aéreo')}
              >
                FOB
              </SelectItem>
              <SelectItem
                key='cif'
                value='cif'
                description={t('No incluye el costo de flete marítimo/aéreo')}
              >
                CIF
              </SelectItem>
            </Select>
            <Select
              label={t('Separación Datos Propios')}
              // description='Separa los datos propios por etiqueta o cliente'
              selectedKeys={separation ? [separation] : []}
              onSelectionChange={(keys) =>
                setSeparation(
                  Array.from(keys)[0] as 'none' | 'by_label' | 'by_receiver'
                )
              }
              className='w-full'
              isDisabled={!canUseCurrentChartType || loading}
            >
              <SelectItem key='none' value='none' textValue={t('No Separar')}>
                {t('No Separar')}
              </SelectItem>
              <SelectItem
                key='by_label'
                value='by_label'
                textValue={t('Por Etiqueta')}
              >
                {t('Por Etiqueta')}
              </SelectItem>
              <SelectItem
                key='by_receiver'
                value='by_receiver'
                textValue={t('Por Cliente')}
              >
                {t('Por Cliente')}
              </SelectItem>
            </Select>

            {separation !== 'none' && separationItems.length > 0 && (
              <Select
                label={
                  separation === 'by_label'
                    ? t('Etiquetas a mostrar')
                    : t('Clientes a mostrar')
                }
                selectionMode='multiple'
                selectedKeys={new Set(selectedSeparationItems)}
                onSelectionChange={(keys) =>
                  setSelectedSeparationItems(Array.from(keys) as string[])
                }
                className='w-full'
                isDisabled={!canUseCurrentChartType || loading}
              >
                {separationItems.map((item) => (
                  <SelectItem key={item} value={item}>
                    {item}
                  </SelectItem>
                ))}
              </Select>
            )}

            <div className='relative'>
              <Select
                label={t('Categoría de Etiquetas')}
                selectionMode='multiple'
                selectedKeys={new Set(labelCategoryFilter)}
                onSelectionChange={(keys) =>
                  setLabelCategoryFilter(Array.from(keys) as string[])
                }
                className='w-full'
                isDisabled={
                  !canUseSpeciesSpecificControls ||
                  loading ||
                  complianceLoading ||
                  !labelCompliance?.hasLabels ||
                  !labelCompliance?.isCompliant
                }
              >
                <SelectItem key='PREMIUM' value='PREMIUM'>
                  PREMIUM
                </SelectItem>
                <SelectItem key='ESTANDAR' value='ESTANDAR'>
                  ESTANDAR
                </SelectItem>
                <SelectItem key='REGULAR' value='REGULAR'>
                  REGULAR
                </SelectItem>
              </Select>
              {labelCompliance &&
                (!labelCompliance.hasLabels ||
                  !labelCompliance.isCompliant) && (
                  <Popover placement='left' showArrow>
                    <PopoverTrigger>
                      <button
                        className='absolute right-9 top-1/2 -translate-y-1/2 text-warning-500 hover:text-warning-600 transition-colors z-10'
                        type='button'
                      >
                        <Icon icon='tabler:alert-triangle-filled' width={16} />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent>
                      <div className='p-3 max-w-xs'>
                        <p className='text-sm font-semibold mb-2'>
                          {t('Categoría de Etiquetas')}
                        </p>
                        <p className='text-xs text-default-500 mb-3'>
                          {t(
                            'Filtra los datos propios e industria por categoría de etiqueta (PREMIUM, ESTANDAR, REGULAR).'
                          )}
                        </p>
                        <div className='border-t border-default-200 pt-2'>
                          {!labelCompliance.hasLabels ? (
                            <p className='text-xs text-warning-600'>
                              {t(
                                'Declara tus etiquetas oficiales en Configuración > Etiquetas Oficiales para habilitar este filtro.'
                              )}
                            </p>
                          ) : (
                            <>
                              <p className='text-xs text-warning-600 mb-1'>
                                {t(
                                  'Tienes etiquetas en liquidaciones que no están declaradas:'
                                )}
                              </p>
                              <div className='flex flex-wrap gap-1 mt-1'>
                                {labelCompliance.nonCompliantLabels.map(
                                  (label) => (
                                    <Chip key={label} size='sm' variant='flat'>
                                      {label}
                                    </Chip>
                                  )
                                )}
                              </div>
                              <p className='text-xs text-default-400 mt-2'>
                                {t(
                                  'Declara estas etiquetas en Configuración > Etiquetas Oficiales.'
                                )}
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
            </div>

            <div className='mt-4'>
              <Switch
                size='sm'
                isSelected={includeRevenues}
                onValueChange={setIncludeRevenues}
                isDisabled={!canUseCurrentChartType || loading}
              >
                <span className='text-sm'>
                  {t('Considerar ingresos adicionales')}
                </span>
              </Switch>
            </div>

            <p className='text-sm text-default-500 mb-1'>{t('Filtros')}</p>
            <SeasonFilter disabled={!canUseCurrentChartType || loading} />

            <Tabs
              fullWidth
              size='sm'
              className='mb-4'
              aria-label={t('Filtrar por')}
              selectedKey={filterMode}
              onSelectionChange={(k) =>
                handleFilterModeChange(k as 'country' | 'market' | 'shiptrip')
              }
              isDisabled={
                !canUseSpeciesSpecificControls ||
                loading ||
                marketsAndVarietiesLoading
              }
            >
              <Tab key='country' title={t('Países')} />
              <Tab key='market' title={t('Mercados')} />
              <Tab key='shiptrip' title={t('Viajes')} />
            </Tabs>

            {/* Confirmation buttons for multiselect filters */}
            {hasPendingChanges && (
              <div className='flex gap-2 mt-4 mb-4'>
                <Button
                  size='sm'
                  color='primary'
                  onClick={confirmFilterChanges}
                  className='flex-1'
                  isDisabled={
                    !canUseSpeciesSpecificControls ||
                    loading ||
                    marketsAndVarietiesLoading
                  }
                >
                  {t('Confirmar')}
                </Button>
                <Button
                  size='sm'
                  variant='flat'
                  onClick={clearPendingChanges}
                  className='flex-1'
                  isDisabled={
                    !canUseSpeciesSpecificControls ||
                    loading ||
                    marketsAndVarietiesLoading
                  }
                >
                  {t('Limpiar')}
                </Button>
              </div>
            )}

            {filterMode === 'country' && (
              <Select
                label={t('Países')}
                selectionMode='multiple'
                selectedKeys={new Set(pendingCountry)}
                onSelectionChange={(keys) =>
                  handleFilterChange('country', Array.from(keys) as string[])
                }
                className='w-full'
                isDisabled={
                  !canUseSpeciesSpecificControls ||
                  loading ||
                  marketsAndVarietiesLoading
                }
              >
                {countries.map((country) => (
                  <SelectItem key={country} value={country}>
                    {country}
                  </SelectItem>
                ))}
              </Select>
            )}

            {filterMode === 'market' && (
              <Select
                label={t('Mercados')}
                selectionMode='multiple'
                selectedKeys={new Set(pendingMarket)}
                onSelectionChange={(keys) =>
                  handleFilterChange('market', Array.from(keys) as string[])
                }
                className='w-full'
                isDisabled={
                  !canUseSpeciesSpecificControls ||
                  loading ||
                  marketsAndVarietiesLoading
                }
              >
                {markets.map((market) => (
                  <SelectItem key={market.id} value={market.id}>
                    {market.name}
                  </SelectItem>
                ))}
              </Select>
            )}

            {filterMode === 'shiptrip' && (
              <Select
                label={t('Viajes')}
                selectionMode='multiple'
                selectedKeys={new Set(pendingShipTrip)}
                onSelectionChange={(keys) =>
                  handleFilterChange('shiptrip', Array.from(keys) as string[])
                }
                className='w-full'
                isDisabled={
                  !canUseSpeciesSpecificControls ||
                  loading ||
                  marketsAndVarietiesLoading
                }
                popoverProps={{
                  className: 'min-w-[500px] max-w-[700px]',
                  // You can adjust min-w and max-w as needed
                }}
                renderValue={(items) => {
                  return (
                    <div className='flex flex-wrap gap-2'>
                      <div className='bg-default-100 px-2 py-1 rounded text-sm'>
                        {items.length}{' '}
                        {items.length === 1 ? t('viaje') : t('viajes')}
                      </div>
                    </div>
                  );
                }}
              >
                {shipTrips
                  .sort((a, b) => a.ship_name.localeCompare(b.ship_name))
                  .map((shipTrip) => (
                    <SelectItem
                      key={shipTrip.id.toString()}
                      value={shipTrip.id.toString()}
                    >
                      <span className='whitespace-normal'>
                        {`${shipTrip.ship_name} - ${shipTrip.market_name} - ${formatDate(shipTrip.eta, 'dd MMM yyyy')}`}
                      </span>
                    </SelectItem>
                  ))}
              </Select>
            )}

            <Select
              label={t('Incluir Siniestros')}
              selectedKeys={[sinisterFilter]}
              onSelectionChange={(keys) =>
                setSinisterFilter(Array.from(keys)[0] as typeof sinisterFilter)
              }
              className='w-full'
              isDisabled={!canUseCurrentChartType || loading}
            >
              <SelectItem key='all' value='all'>
                {t('Sí')}
              </SelectItem>
              <SelectItem key='non_sinister' value='non_sinister'>
                {t('No')}
              </SelectItem>
              <SelectItem key='sinister' value='sinister'>
                {t('Sólo Siniestros')}
              </SelectItem>
            </Select>

            <p className='text-sm text-default-500 mb-1'>{t('Unidades')}</p>
            <Select
              label={t('Unidad de precio')}
              value={priceView}
              onChange={(e) => setPriceView(e.target.value as 'kg' | 'box')}
              defaultSelectedKeys={['kg']}
              className='w-full'
              isDisabled={!canUseCurrentChartType || loading}
            >
              <SelectItem key='kg' value='kg' textValue={t('Precio por kg')}>
                {t('Precio por kg')}
              </SelectItem>
              <SelectItem
                key='box'
                value='box'
                textValue={t('Precio por caja - 5kgs')}
              >
                {t('Precio por caja - 5kgs')}
              </SelectItem>
            </Select>

            <p className='text-sm text-default-500 mb-1'>
              {t('Visualización')}
            </p>
            <Select
              label={t('Tipo de visualización')}
              value={visualization}
              onChange={(e) =>
                setVisualization(e.target.value as 'chart' | 'table')
              }
              defaultSelectedKeys={['chart']}
              className='w-full'
              isDisabled={!canUseCurrentChartType || loading}
            >
              <SelectItem key='chart' value='chart' textValue={t('Gráficos')}>
                {t('Gráficos')}
              </SelectItem>
              <SelectItem key='table' value='table' textValue={t('Tablas')}>
                {t('Tablas')}
              </SelectItem>
            </Select>
          </div>
        </div>
      </ModalSlideFilter>

      <MethologyFobExportersModal
        isOpen={isMethodologyModalOpen}
        onClose={() => setIsMethodologyModalOpen(false)}
      />

      <PageLayout>
        <Header
          title={t('Comparativo Resultados FOB/CIF Industria')}
          description={t(
            'Analiza los precios FOB/CIF entre exportadoras que usan CherryMarket desde múltiples perspectivas.'
          )}
        />

        <div className='mb-4'>
          <FiltersToggle
            isOpen={isFilterOpen}
            onToggle={() => setIsFilterOpen(!isFilterOpen)}
            label={isFilterOpen ? t('Ocultar filtros') : t('Mostrar filtros')}
          />
        </div>

        {userProfile?.exporter?.hasDemo && (
          <Alert className='mb-4'>
            {t(
              'Estás viendo una versión de prueba. Contrata CherryMarket para ver los datos completos.'
            )}
          </Alert>
        )}

        {!hasAccess && (
          <Alert variant='warning' className='mb-4'>
            {t(
              'Tu organización no tiene permisos para acceder al módulo Industria para esta especie/temporada.'
            )}
          </Alert>
        )}

        {/* Chart Type Tabs */}
        <div>
          <Tabs
            selectedKey={selectedChartType}
            onSelectionChange={(key) => {
              if (userProfile?.exporter?.hasDemo && key !== 'evolution') return;
              if (!isSupportedSpecies && key !== 'global') return;
              setSelectedChartType(key as ChartType);
            }}
            className='w-full'
            variant='underlined'
            isDisabled={!hasAccess}
          >
            {!isSupportedSpecies && (
              <Tab key='global' title={renderTabTitle(t('Global'))}></Tab>
            )}
            <Tab
              key='evolution'
              title={renderTabTitle(
                t('Evolución por Semana'),
                !isSupportedSpecies
              )}
              isDisabled={!isSupportedSpecies}
            ></Tab>

            <Tab
              key='caliber'
              title={renderTabTitle(t('Por Calibre'), !isSupportedSpecies)}
              isDisabled={
                !isSupportedSpecies || !!userProfile?.exporter?.hasDemo
              }
            ></Tab>
            <Tab
              key='variety'
              title={renderTabTitle(t('Por Variedad'), !isSupportedSpecies)}
              isDisabled={
                !isSupportedSpecies || !!userProfile?.exporter?.hasDemo
              }
            ></Tab>
          </Tabs>
        </div>

        <Card
          className={`p-4 mt-4 ${!canUseCurrentChartType ? 'blur-md pointer-events-none select-none' : ''}`}
        >
          <CardHeader className='flex-col gap-4'>
            <div className='flex justify-between items-center w-full'>
              <div className='flex items-center gap-2'>
                <h2 className='text-lg font-semibold'>
                  {getChartTitle(selectedChartType)}
                </h2>
                <ChartDownloadButton
                  chartRef={industryChartRef}
                  title={getChartTitle(selectedChartType)}
                />
              </div>
              <Button
                size='sm'
                variant='flat'
                startContent={<Icon icon='tabler:download' />}
                onClick={() => exportToExcel(selectedChartType)}
                isDisabled={!canUseCurrentChartType}
              >
                {t('Exportar a Excel')}
              </Button>
            </div>

            {/* Dynamic controls based on chart type */}
            <div className='flex gap-4 w-full'>
              {/* Variety selector - always shown */}
              {(selectedChartType === 'caliber' ||
                selectedChartType === 'evolution') && (
                <Select
                  label={t('Variedad')}
                  selectedKeys={selectedVariety ? [selectedVariety] : []}
                  onSelectionChange={(keys) =>
                    setSelectedVariety(Array.from(keys)[0] as string)
                  }
                  className='flex-1'
                  isDisabled={
                    marketsAndVarietiesLoading ||
                    loading ||
                    userProfile?.exporter?.hasDemo ||
                    !canUseSpeciesSpecificControls
                  }
                  description={
                    userProfile?.exporter?.hasDemo
                      ? t('Contrata CherryMarket para ver todas las variedades')
                      : ''
                  }
                >
                  {varieties.map((varietyData) => (
                    <SelectItem
                      key={varietyData.variety}
                      value={varietyData.variety}
                    >
                      {varietyData.variety}
                    </SelectItem>
                  ))}
                </Select>
              )}

              {/* Caliber selector - shown for variety and evolution charts */}
              {(selectedChartType === 'variety' ||
                selectedChartType === 'evolution') && (
                <Select
                  label={t('Calibre')}
                  selectedKeys={selectedCaliber ? [selectedCaliber] : []}
                  onSelectionChange={(keys) =>
                    setSelectedCaliber(Array.from(keys)[0] as string)
                  }
                  className='flex-1'
                  isDisabled={
                    loading ||
                    userProfile?.exporter?.hasDemo ||
                    !canUseSpeciesSpecificControls
                  }
                  description={
                    userProfile?.exporter?.hasDemo
                      ? t('Contrata CherryMarket para ver todos los calibres')
                      : ''
                  }
                >
                  {availableCalibers.map((caliber) => (
                    <SelectItem key={caliber} value={caliber}>
                      {caliber}
                    </SelectItem>
                  ))}
                </Select>
              )}

              {/* Week selector - shown for caliber and variety charts */}
              {(selectedChartType === 'caliber' ||
                selectedChartType === 'variety') && (
                <Select
                  label={t('Semana')}
                  selectedKeys={selectedWeek ? [selectedWeek.toString()] : []}
                  onSelectionChange={(keys) => {
                    setSelectedWeek(parseInt(Array.from(keys)[0] as string));
                  }}
                  className='flex-1'
                  isDisabled={
                    loading ||
                    userProfile?.exporter?.hasDemo ||
                    !canUseSpeciesSpecificControls
                  }
                  description={
                    userProfile?.exporter?.hasDemo
                      ? t('Contrata CherryMarket para ver todas las semanas')
                      : ''
                  }
                >
                  {configuredWeeks.map((week) => (
                    <SelectItem key={week} value={week}>
                      {String(week)}
                    </SelectItem>
                  ))}
                </Select>
              )}
            </div>
          </CardHeader>

          <CardBody>
            {!canUseCurrentChartType ? (
              <EmptyState
                title={t(
                  'Esta vista no está disponible para la especie seleccionada'
                )}
                description={t(
                  'Por favor selecciona cereza o nectarine en el filtro de especies para ver este análisis'
                )}
              />
            ) : loading || marketsAndVarietiesLoading ? (
              <LoadingState />
            ) : visualization === 'chart' ? (
              (() => {
                const categories =
                  selectedChartType === 'global'
                    ? [t('Global')]
                    : selectedChartType === 'caliber'
                      ? availableCalibers
                      : selectedChartType === 'variety'
                        ? varieties.map((v) => v.variety)
                        : weeks.map((w) => w.split('-W')[1]);

                const { series, scatterColors } =
                  prepareSeriesData(selectedChartType);

                // Safety check: don't render chart if no data or categories
                if (!categories.length || !series.length) {
                  return (
                    <EmptyState
                      title={t('No hay datos disponibles para mostrar')}
                    />
                  );
                }

                return (
                  <div ref={industryChartRef}>
                    <ReactApexChart
                      options={getChartOptions(
                        selectedChartType,
                        categories,
                        priceView,
                        scatterColors
                      )}
                      series={series}
                      type='boxPlot'
                      height={450}
                    />
                  </div>
                );
              })()
            ) : (
              <TableView type={selectedChartType} />
            )}
          </CardBody>
        </Card>
      </PageLayout>
    </div>
  );
};

export default SettlementsComparativeExportersUnified;
